/**
 * MiddlewareManager - Manages bidirectional middleware pipeline
 * 
 * Executes middleware hooks in order:
 * 1. onPreCollect - before event collection
 * 2. onPostCollect - after event collection
 * 3. onPreSend - before sending to backend
 */

import type { 
  Middleware, 
  PreCollectEvent, 
  CollectedEvent, 
  EventBatch,
  MiddlewareContext 
} from '../middleware/types.js';

export class MiddlewareManager {
  private middleware: Middleware[] = [];
  private context: MiddlewareContext;

  constructor(context: MiddlewareContext) {
    this.context = context;
  }

  /**
   * Register a middleware
   */
  register(middleware: Middleware): void {
    // Check if middleware with same name already exists
    const existingIndex = this.middleware.findIndex(m => m.name === middleware.name);
    
    if (existingIndex !== -1) {
      // Replace existing middleware
      this.middleware[existingIndex] = middleware;
      this.log(`Replaced middleware: ${middleware.name}`);
    } else {
      this.middleware.push(middleware);
      this.log(`Registered middleware: ${middleware.name}`);
    }
  }

  /**
   * Unregister a middleware by name
   */
  unregister(name: string): boolean {
    const index = this.middleware.findIndex(m => m.name === name);
    
    if (index !== -1) {
      this.middleware.splice(index, 1);
      this.log(`Unregistered middleware: ${name}`);
      return true;
    }
    
    return false;
  }

  /**
   * Execute pre-collect hooks
   * Returns null if any middleware filters out the event
   */
  executePreCollect(event: PreCollectEvent): PreCollectEvent | null {
    let currentEvent: PreCollectEvent | null = event;

    for (const mw of this.middleware) {
      if (mw.enabled === false) continue;
      
      if (mw.onPreCollect && currentEvent !== null) {
        try {
          currentEvent = mw.onPreCollect(currentEvent);
          
          // If middleware returns null, stop processing
          if (currentEvent === null) {
            this.log(`Event filtered by middleware: ${mw.name}`);
            return null;
          }
        } catch (error) {
          console.error(`[MiddlewareManager] Error in ${mw.name}.onPreCollect:`, error);
          // Continue to next middleware on error
        }
      }
    }

    return currentEvent;
  }

  /**
   * Execute post-collect hooks
   * Enriches event with additional data
   */
  executePostCollect(event: CollectedEvent): CollectedEvent {
    let currentEvent = event;

    for (const mw of this.middleware) {
      if (mw.enabled === false) continue;
      
      if (mw.onPostCollect) {
        try {
          currentEvent = mw.onPostCollect(currentEvent);
        } catch (error) {
          console.error(`[MiddlewareManager] Error in ${mw.name}.onPostCollect:`, error);
          // Continue to next middleware on error
        }
      }
    }

    return currentEvent;
  }

  /**
   * Execute pre-send hooks
   * Transforms batch before sending
   */
  executePreSend(batch: EventBatch): EventBatch {
    let currentBatch = batch;

    for (const mw of this.middleware) {
      if (mw.enabled === false) continue;
      
      if (mw.onPreSend) {
        try {
          currentBatch = mw.onPreSend(currentBatch);
        } catch (error) {
          console.error(`[MiddlewareManager] Error in ${mw.name}.onPreSend:`, error);
          // Continue to next middleware on error
        }
      }
    }

    return currentBatch;
  }

  /**
   * Get all registered middleware
   */
  getMiddleware(): Middleware[] {
    return [...this.middleware];
  }

  /**
   * Clear all middleware
   */
  clear(): void {
    this.middleware = [];
    this.log('Cleared all middleware');
  }

  /**
   * Update context (for userId updates, etc.)
   */
  updateContext(updates: Partial<MiddlewareContext>): void {
    this.context = { ...this.context, ...updates };
  }

  private log(message: string): void {
    if (this.context.debug) {
      console.log(`[MiddlewareManager] ${message}`);
    }
  }
}

